import React, { useState } from 'react';
import confetti from 'canvas-confetti';

const MANANTIAL_PALETTE = ['#912b2b', '#b83a2e', '#ffffff', '#0a0a0a', '#6d1e1e'];

function fireManantialConfetti() {
  const defaults = {
    spread: 360,
    ticks: 100,
    gravity: 0.8,
    decay: 0.94,
    startVelocity: 30,
    colors: MANANTIAL_PALETTE,
    zIndex: 9999
  };
  confetti({ ...defaults, particleCount: 80, origin: { x: 0.5, y: 0.55 }, scalar: 1.1 });
  setTimeout(() => {
    confetti({ ...defaults, particleCount: 40, angle: 60, spread: 80, origin: { x: 0.1, y: 0.7 } });
  }, 120);
  setTimeout(() => {
    confetti({ ...defaults, particleCount: 40, angle: 120, spread: 80, origin: { x: 0.9, y: 0.7 } });
  }, 240);
}

const PHASE_LABELS = {
  locating:    'Obteniendo ubicación…',
  'locating-ip': 'Buscando ubicación (IP)…',
  saving:      'Registrando…',
  refreshing:  'Casi listo…'
};

export default function MarcacionPanel({ myDay, onMark, disabled = false }) {
  const [comentario, setComentario] = useState('');
  const [savingTipo, setSavingTipo] = useState(null);
  const [savingPhase, setSavingPhase] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const ingresoCanonico = (myDay.ingresos || []).find((m) => m.is_canonical);
  const salidaCanonica = (myDay.salidas || []).find((m) => m.is_canonical);
  const yaIngreso = Boolean(ingresoCanonico);
  const yaSalida = Boolean(salidaCanonica);
  const isAnySaving = Boolean(savingTipo);

  const handle = async (tipo) => {
    if (savingTipo) return;

    if (tipo === 'ingreso' && yaIngreso) {
      const ok = window.confirm(
        `Ya marcaste ingreso a las ${formatHora(ingresoCanonico.timestamp_iso)}. ¿Reemplazar la marca?`
      );
      if (!ok) return;
    }
    if (tipo === 'salida' && yaSalida) {
      const ok = window.confirm(
        `Ya marcaste salida a las ${formatHora(salidaCanonica.timestamp_iso)}. ¿Reemplazar la marca?`
      );
      if (!ok) return;
    }

    setFeedback(null);
    setSavingTipo(tipo);
    setSavingPhase('locating');
    try {
      const result = await onMark({
        tipo,
        comentario: comentario.trim() || undefined,
        onProgress: (phase) => setSavingPhase(phase)
      });
      setFeedback({
        kind: 'success',
        text: tipo === 'ingreso'
          ? `Ingreso ${formatHora(result.timestamp_iso)} — ${result.tardanza_minutos > 0 ? `${result.tardanza_minutos} min tarde` : 'puntual'}`
          : `Salida ${formatHora(result.timestamp_iso)} registrada`
      });
      if (tipo === 'ingreso' && (result.tardanza_minutos || 0) === 0) {
        fireManantialConfetti();
      }
      setComentario('');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Error al marcar.';
      const hint = err?.hint || null;
      const canRetry = err?.name === 'LocationError' && ['timeout', 'unavailable', 'unknown'].includes(err?.kind);
      setFeedback({ kind: 'error', text: msg, hint, retryTipo: canRetry ? tipo : null });
    } finally {
      setSavingTipo(null);
      setSavingPhase(null);
    }
  };

  const labelBtn = (tipo) => {
    if (savingTipo === tipo) return PHASE_LABELS[savingPhase] || 'Guardando…';
    if (tipo === 'ingreso') return yaIngreso ? 'Re-marcar Ingreso' : 'Marcar Ingreso';
    return yaSalida ? 'Re-marcar Salida' : 'Marcar Salida';
  };

  return (
    <>
      <section className="section">
        <h2 className="section-label">Nota</h2>
        <textarea
          className="note-input"
          placeholder="Opcional — añade contexto si es necesario."
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          maxLength={300}
          disabled={isAnySaving}
        />
      </section>

      {feedback && (
        <div className={`alert ${feedback.kind}`}>
          <span className="alert-tag">{feedback.kind === 'success' ? 'OK' : 'Error'}</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
            <span>{feedback.text}</span>
            {feedback.hint && (
              <span style={{ fontSize: '0.82rem', opacity: 0.85, lineHeight: 1.5 }}>
                {feedback.hint}
              </span>
            )}
            {feedback.retryTipo && (
              <button
                type="button"
                onClick={() => handle(feedback.retryTipo)}
                disabled={isAnySaving}
                style={{
                  alignSelf: 'flex-start',
                  marginTop: 4,
                  padding: '6px 14px',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  border: '1px solid currentColor',
                  borderRadius: 6,
                  background: 'transparent',
                  color: 'inherit',
                  cursor: isAnySaving ? 'not-allowed' : 'pointer'
                }}
              >
                Reintentar
              </button>
            )}
          </div>
        </div>
      )}

      <div className="actions">
        <button
          type="button"
          className="action"
          onClick={() => handle('ingreso')}
          disabled={disabled || isAnySaving}
        >
          <span className="label-pre">{savingTipo === 'ingreso' ? '···' : '01'}</span>
          {labelBtn('ingreso')}
        </button>
        <button
          type="button"
          className="action salida"
          onClick={() => handle('salida')}
          disabled={disabled || isAnySaving || !yaIngreso}
          title={!yaIngreso ? 'Debes marcar tu ingreso antes de la salida.' : undefined}
        >
          <span className="label-pre">{savingTipo === 'salida' ? '···' : '02'}</span>
          {labelBtn('salida')}
        </button>
        {!yaIngreso && (
          <div className="salida-lock-hint">
            Marca tu ingreso primero para habilitar la salida.
          </div>
        )}
      </div>
    </>
  );
}

function formatHora(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
}
